type NgrokHeaderDecisionArgs = {
  isNgrokUrl: boolean;
  requestUrl?: string;
  baseUrl?: string;
  apiBaseUrl: string;
};

const ABSOLUTE_URL_PATTERN = /^[a-z][a-z\d+\-.]*:\/\//i;

const normalizeBaseUrl = (url: string): string => url.replace(/\/+$/, "");
const isAbsoluteUrl = (url: string): boolean => ABSOLUTE_URL_PATTERN.test(url);

const resolveRequestUrl = (
  requestUrl: string | undefined,
  baseUrl: string | undefined,
): string | null => {
  if (!requestUrl) return null;
  if (isAbsoluteUrl(requestUrl)) return requestUrl;
  if (!baseUrl) return requestUrl;

  return `${normalizeBaseUrl(baseUrl)}/${requestUrl.replace(/^\/+/, "")}`;
};

const hasSameOrigin = (requestUrl: string, apiBaseUrl: string): boolean => {
  if (!isAbsoluteUrl(apiBaseUrl)) return false;

  try {
    return new URL(requestUrl).origin === new URL(apiBaseUrl).origin;
  } catch {
    return false;
  }
};

const isSameRelativePathOrChild = (
  requestUrl: string,
  apiBaseUrl: string,
): boolean => {
  const normalizedBaseUrl = normalizeBaseUrl(apiBaseUrl);
  return (
    requestUrl === normalizedBaseUrl ||
    requestUrl.startsWith(`${normalizedBaseUrl}/`)
  );
};

/**
 * Ngrok's skip-browser-warning header is only valid for requests to the Dale API.
 * Adding it to third-party APIs forces a CORS preflight that many public APIs reject.
 */
export const shouldAddNgrokSkipBrowserWarning = ({
  isNgrokUrl,
  requestUrl,
  baseUrl,
  apiBaseUrl,
}: NgrokHeaderDecisionArgs): boolean => {
  if (!isNgrokUrl) return false;

  const resolvedRequestUrl = resolveRequestUrl(requestUrl, baseUrl);
  if (!resolvedRequestUrl) return false;

  if (!isAbsoluteUrl(resolvedRequestUrl)) {
    return isSameRelativePathOrChild(resolvedRequestUrl, apiBaseUrl);
  }

  return hasSameOrigin(resolvedRequestUrl, apiBaseUrl);
};
